import React, { useState } from 'react';
import axios from 'axios';
import { Banknote, CreditCard, Wallet } from 'lucide-react';
import Receipt from '../components/Receipt';
import OrderBuilder from '../components/OrderBuilder';
import { useToast, PageHeader, Button, Input } from '../components/ui';
import { fmtMoney } from '../utils/currency';

// The cashier's station: the shared OrderBuilder (menu, cart, customer)
// plus the payment + receipt flow that only lives here.

const PAYMENT_METHODS = [
  { value: 'cash',           label: 'Cash',    Icon: Banknote },
  { value: 'card',           label: 'Card',    Icon: CreditCard },
  { value: 'digital_wallet', label: 'Digital', Icon: Wallet },
];

const POS = () => {
  const { toast } = useToast();
  const [showPayment, setShowPayment] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [paymentData, setPaymentData] = useState({ method: 'cash', amountReceived: '', tipAmount: '' });

  const handlePlaced = (order, orderType) => {
    setCurrentOrder(order);
    if (orderType === 'delivery') {
      toast.success(`Order ${order.orderNumber} sent to kitchen and rider.`);
    } else {
      setShowPayment(true);
    }
  };

  const processPayment = async () => {
    try {
      const payload = {
        paymentMethod: paymentData.method,
        tipAmount: parseFloat(paymentData.tipAmount) || 0
      };
      if (paymentData.method === 'cash') {
        payload.amountReceived = parseFloat(paymentData.amountReceived) || currentOrder.totalAmount;
      }
      const res = await axios.post(`/api/orders/${currentOrder._id}/pay`, payload, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setCurrentOrder(res.data.order);
      setShowPayment(false);
      setShowReceipt(true);
      setPaymentData({ method: 'cash', amountReceived: '', tipAmount: '' });
    } catch (err) {
      toast.error('Payment failed: ' + (err.response?.data?.msg || err.message));
    }
  };

  const closeReceipt = () => {
    setShowReceipt(false);
    setCurrentOrder(null);
  };

  const closePaymentLater = () => {
    setShowPayment(false);
    setCurrentOrder(null);
  };

  const orderTotalForChange = currentOrder?.totalAmount ?? 0;
  const changeAmount = paymentData.method === 'cash' && paymentData.amountReceived
    ? Math.max(0, parseFloat(paymentData.amountReceived) - orderTotalForChange - (parseFloat(paymentData.tipAmount) || 0))
    : 0;

  return (
    <>
      <PageHeader
        title="Point of Sale"
        subtitle="Build the cart, link a customer, take payment."
      />

      <OrderBuilder payFlow onPlaced={handlePlaced} />

      {/* Payment modal */}
      {showPayment && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover max-w-md w-full">
            <div className="p-6 border-b border-neutral-200">
              <h2 className="text-xl font-bold text-neutral-900">Process Payment</h2>
              <div className="mt-2 flex items-baseline justify-between">
                <p className="text-xs text-neutral-500 font-mono">{currentOrder?.orderNumber}</p>
                <p className="text-2xl font-bold text-neutral-900 tabular-nums">{fmtMoney(orderTotalForChange, true)}</p>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Payment method</label>
                <div className="grid grid-cols-3 gap-2">
                  {PAYMENT_METHODS.map(({ value, label, Icon }) => {
                    const active = paymentData.method === value;
                    return (
                      <button
                        key={value}
                        onClick={() => setPaymentData({ ...paymentData, method: value })}
                        className={`
                          flex flex-col items-center gap-1.5 p-3 rounded-md border transition-all
                          ${active
                            ? 'border-primary-500 bg-primary-50 text-primary-700 ring-2 ring-primary-200'
                            : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'}
                        `}
                      >
                        <Icon size={18} />
                        <span className="text-xs font-medium">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {paymentData.method === 'cash' && (
                <Input
                  label="Amount received"
                  type="number"
                  value={paymentData.amountReceived}
                  onChange={(e) => setPaymentData({ ...paymentData, amountReceived: e.target.value })}
                  placeholder={orderTotalForChange.toFixed(2)}
                  helper={changeAmount > 0 ? `Change to give: ${fmtMoney(changeAmount, true)}` : undefined}
                />
              )}

              <Input
                label="Tip (optional)"
                type="number"
                value={paymentData.tipAmount}
                onChange={(e) => setPaymentData({ ...paymentData, tipAmount: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="px-6 py-4 bg-neutral-50 rounded-b-lg flex gap-2 border-t border-neutral-200">
              <Button variant="secondary" size="md" fullWidth onClick={closePaymentLater}>
                Close (pay later)
              </Button>
              <Button variant="success" size="md" fullWidth onClick={processPayment}>
                Confirm Payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {showReceipt && <Receipt order={currentOrder} onClose={closeReceipt} />}
    </>
  );
};

export default POS;
